import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Alert, Chip, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, LinearProgress, Collapse, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Tab, Tabs,
} from '@mui/material';
import {
  CheckCircle, Cancel, AccessTime, Info, Refresh,
  Warning, VerifiedUser, Person, Home,
  CreditCard, Badge, Close, Search,
  FilterList, ExpandMore, ExpandLess, Shield,
  Fingerprint, ContentCopy, Visibility,
  Flag, StickyNote2, Timeline, Description,
  QuestionAnswer, HourglassBottom, GppBad, GppGood,
} from '@mui/icons-material';
import { kycAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const STATUS_CONF = {
  pending:      { label: 'Pending',      color: '#d97706', bg: '#fef3c7', border: '#fbbf24', icon: <AccessTime sx={{ fontSize: 14 }} /> },
  under_review: { label: 'Under Review', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd', icon: <Info sx={{ fontSize: 14 }} /> },
  approved:     { label: 'Approved',     color: '#16a34a', bg: '#dcfce7', border: '#22c55e', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  rejected:     { label: 'Rejected',     color: '#dc2626', bg: '#fef2f2', border: '#f87171', icon: <Cancel sx={{ fontSize: 14 }} /> },
  expired:      { label: 'Expired',      color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', icon: <Warning sx={{ fontSize: 14 }} /> },
};

const DOC_TYPE_CONF = {
  aadhaar:         { label: 'Aadhaar',          icon: <Fingerprint sx={{ fontSize: 14 }} />, color: '#7c3aed' },
  pan:             { label: 'PAN Card',         icon: <Badge sx={{ fontSize: 14 }} />,       color: '#2563eb' },
  voter_id:        { label: 'Voter ID',         icon: <CreditCard sx={{ fontSize: 14 }} />,  color: '#0891b2' },
  passport:        { label: 'Passport',         icon: <Description sx={{ fontSize: 14 }} />, color: '#059669' },
  driving_license: { label: 'Driving License',  icon: <Description sx={{ fontSize: 14 }} />, color: '#d97706' },
};

const RISK_FLAGS = [
  { key: 'duplicate_aadhaar', label: 'Duplicate Aadhaar', color: '#dc2626', bg: '#fef2f2', icon: <ContentCopy sx={{ fontSize: 13 }} /> },
  { key: 'duplicate_pan',     label: 'Duplicate PAN',     color: '#9333ea', bg: '#f3e8ff', icon: <ContentCopy sx={{ fontSize: 13 }} /> },
  { key: 'address_mismatch',  label: 'Address Mismatch',  color: '#d97706', bg: '#fef3c7', icon: <Home sx={{ fontSize: 13 }} /> },
  { key: 'suspicious_cluster',label: 'Suspicious Cluster', color: '#0f172a', bg: '#e2e8f0', icon: <GppBad sx={{ fontSize: 13 }} /> },
];

// Derive risk flags from KYC data (heuristic: client-side only since no API change)
const deriveRiskFlags = (kyc, allSubmissions) => {
  const flags = [];
  if (!kyc) return flags;

  // Duplicate Aadhaar: same doc number, different farmer
  if (kyc.document_type === 'aadhaar') {
    const dup = allSubmissions.find(s =>
      s.id !== kyc.id && s.document_type === 'aadhaar' &&
      s.document_number_masked === kyc.document_number_masked && s.farmer_id !== kyc.farmer_id
    );
    if (dup) flags.push('duplicate_aadhaar');
  }

  // Duplicate PAN
  if (kyc.document_type === 'pan') {
    const dup = allSubmissions.find(s =>
      s.id !== kyc.id && s.document_type === 'pan' &&
      s.document_number_masked === kyc.document_number_masked && s.farmer_id !== kyc.farmer_id
    );
    if (dup) flags.push('duplicate_pan');
  }

  // Address mismatch heuristic: if farmer has multiple KYCs with different addresses (not 100% reliable)
  const farmerKYCs = allSubmissions.filter(s => s.farmer_id === kyc.farmer_id);
  if (farmerKYCs.length > 1) {
    flags.push('address_mismatch');
  }

  // Suspicious cluster: multiple submissions from same farmer in short time
  if (farmerKYCs.length >= 3) {
    flags.push('suspicious_cluster');
  }

  return flags;
};

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const kycAge = (submittedAt) => {
  if (!submittedAt) return '—';
  const ms = Date.now() - new Date(submittedAt).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

// ═══════════════════════════════════════════════════════════════
// KPI CARD
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, onClick, active }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 2, cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid #e2e8f0',
      transition: 'all 0.2s ease',
      background: active ? `${color}08` : '#fff',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20`, borderColor: color } : {},
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// KYC TIMELINE (Submitted → Under Review → Approved/Rejected)
// ═══════════════════════════════════════════════════════════════

const KYCTimeline = ({ kyc }) => {
  if (!kyc) return null;

  const steps = [
    {
      label: 'Submitted',
      time: kyc.submitted_at,
      color: '#2563eb',
      icon: <Description sx={{ fontSize: 12 }} />,
      done: true,
    },
    {
      label: 'Under Review',
      time: kyc.verification_status !== 'pending' ? (kyc.updated_at || kyc.submitted_at) : null,
      color: '#7c3aed',
      icon: <HourglassBottom sx={{ fontSize: 12 }} />,
      done: ['under_review', 'approved', 'rejected'].includes(kyc.verification_status),
    },
    {
      label: kyc.verification_status === 'rejected' ? 'Rejected' : 'Approved',
      time: kyc.verified_at,
      color: kyc.verification_status === 'rejected' ? '#dc2626' : '#16a34a',
      icon: kyc.verification_status === 'rejected' ? <Cancel sx={{ fontSize: 12 }} /> : <GppGood sx={{ fontSize: 12 }} />,
      done: ['approved', 'rejected'].includes(kyc.verification_status),
    },
  ];

  return (
    <Box>
      <Box className="flex items-center gap-1.5 mb-1.5">
        <Timeline sx={{ fontSize: 14, color: '#2563eb' }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          KYC Pipeline
        </Typography>
      </Box>
      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Horizontal steps */}
          <Box className="flex items-center">
            {steps.map((step, i) => (
              <React.Fragment key={i}>
                <Box className="flex flex-col items-center" sx={{ flex: 1 }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: '50%',
                    bgcolor: step.done ? step.color : '#e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.3s ease',
                  }}>
                    {React.cloneElement(step.icon, { sx: { fontSize: 12, color: step.done ? '#fff' : '#94a3b8' } })}
                  </Box>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: step.done ? step.color : '#94a3b8', mt: 0.5, textAlign: 'center' }}>
                    {step.label}
                  </Typography>
                  {step.time && step.done && (
                    <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textAlign: 'center' }}>
                      {fmtDate(step.time)}
                    </Typography>
                  )}
                </Box>
                {i < steps.length - 1 && (
                  <Box sx={{
                    flex: 0.6, height: 2, bgcolor: steps[i + 1].done ? steps[i + 1].color : '#e2e8f0',
                    borderRadius: 1, mt: -2, transition: 'background 0.3s ease',
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// AUDIT LOG
// ═══════════════════════════════════════════════════════════════

const AuditLog = ({ kyc }) => {
  if (!kyc) return null;

  const events = [];
  events.push({ time: kyc.submitted_at || kyc.created_at, label: 'KYC Submitted', color: '#2563eb', icon: <Description sx={{ fontSize: 12 }} /> });

  if (['under_review', 'approved', 'rejected'].includes(kyc.verification_status)) {
    events.push({
      time: kyc.updated_at || kyc.submitted_at,
      label: 'Review Started',
      color: '#7c3aed',
      icon: <HourglassBottom sx={{ fontSize: 12 }} />,
    });
  }

  if (kyc.verification_status === 'approved') {
    events.push({
      time: kyc.verified_at,
      label: `Approved${kyc.verifier_name ? ` by ${kyc.verifier_name}` : kyc.verified_by ? ` by #${kyc.verified_by}` : ''}`,
      color: '#16a34a',
      icon: <CheckCircle sx={{ fontSize: 12 }} />,
    });
  }

  if (kyc.verification_status === 'rejected') {
    events.push({
      time: kyc.verified_at,
      label: `Rejected${kyc.verifier_name ? ` by ${kyc.verifier_name}` : kyc.verified_by ? ` by #${kyc.verified_by}` : ''}`,
      color: '#dc2626',
      icon: <Cancel sx={{ fontSize: 12 }} />,
    });
  }

  if (kyc.rejection_reason) {
    events.push({
      time: kyc.verified_at || kyc.updated_at,
      label: `Reason: "${kyc.rejection_reason}"`,
      color: '#64748b',
      icon: <StickyNote2 sx={{ fontSize: 12 }} />,
    });
  }

  if (kyc.verification_notes) {
    events.push({
      time: kyc.verified_at || kyc.updated_at,
      label: `Note: "${kyc.verification_notes}"`,
      color: '#64748b',
      icon: <StickyNote2 sx={{ fontSize: 12 }} />,
    });
  }

  events.sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));

  return (
    <Box>
      <Box className="flex items-center gap-1.5 mb-1">
        <Shield sx={{ fontSize: 14, color: '#64748b' }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Audit Log
        </Typography>
      </Box>
      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {events.map((evt, i) => (
            <Box key={i} className="flex items-start gap-2" sx={{ mb: i < events.length - 1 ? 1.5 : 0 }}>
              <Box className="flex flex-col items-center" sx={{ minWidth: 14 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: evt.color, flexShrink: 0, mt: 0.3 }} />
                {i < events.length - 1 && <Box sx={{ width: 1, flex: 1, bgcolor: '#e2e8f0', mt: 0.5, minHeight: 14 }} />}
              </Box>
              <Box className="min-w-0 flex-1">
                <Box className="flex items-center gap-1">
                  {evt.icon}
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>{evt.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>{fmtDateTime(evt.time)}</Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// SMALL HELPERS
// ═══════════════════════════════════════════════════════════════

const Section = ({ icon, title, color, children }) => (
  <Box>
    <Box className="flex items-center gap-1 mb-1">
      {React.cloneElement(icon, { sx: { fontSize: 14, color } })}
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', minWidth: 60 }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const KYCReview = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [kycDetails, setKycDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all_pending');
  const [showFilters, setShowFilters] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [clarificationNote, setClarificationNote] = useState('');
  const [clarifyDialogOpen, setClarifyDialogOpen] = useState(false);

  // Preview
  const [previewTab, setPreviewTab] = useState(0);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load Data ──
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);

      const status = statusFilter === 'all_pending' ? null : statusFilter;

      const [submissionsRes, statsRes] = await Promise.all([
        kycAPI.getPending(status),
        kycAPI.getStats(),
      ]);

      setSubmissions(submissionsRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      console.error('Error loading KYC data:', err);
      setSnackbar({ open: true, message: 'Failed to load KYC submissions', severity: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Select KYC & Load Details ──
  const handleSelectKYC = useCallback(async (kyc) => {
    setSelectedKYC(kyc);
    setPreviewTab(0);
    setRejectionReason('');
    setVerificationNotes('');

    try {
      setDetailsLoading(true);
      const response = await kycAPI.getDetails(kyc.id);
      setKycDetails(response.data);
    } catch (err) {
      console.error('Error loading KYC details:', err);
      setKycDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // ── Actions ──
  const handleStartReview = async () => {
    if (!selectedKYC) return;
    try {
      setActionLoading(true);
      await kycAPI.startReview(selectedKYC.id);
      setSnackbar({ open: true, message: 'KYC marked as under review', severity: 'info' });
      await loadData(true);
      // Re-fetch details
      const response = await kycAPI.getDetails(selectedKYC.id);
      setKycDetails(response.data);
      setSelectedKYC(prev => ({ ...prev, verification_status: 'under_review' }));
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to start review', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedKYC) return;
    try {
      setActionLoading(true);
      await kycAPI.approve(selectedKYC.id, {
        verification_status: 'approved',
        verification_notes: verificationNotes || null,
      });
      setSnackbar({ open: true, message: 'KYC approved successfully', severity: 'success' });
      setSelectedKYC(null);
      setKycDetails(null);
      await loadData(true);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to approve KYC', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedKYC || !rejectionReason.trim()) {
      setSnackbar({ open: true, message: 'Rejection reason is required', severity: 'error' });
      return;
    }
    try {
      setActionLoading(true);
      await kycAPI.reject(selectedKYC.id, {
        verification_status: 'rejected',
        rejection_reason: rejectionReason,
        verification_notes: verificationNotes || null,
      });
      setSnackbar({ open: true, message: 'KYC rejected', severity: 'warning' });
      setSelectedKYC(null);
      setKycDetails(null);
      await loadData(true);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to reject KYC', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestClarification = async () => {
    // Use reject with a "clarification" note pattern so farmer can resubmit
    if (!selectedKYC || !clarificationNote.trim()) {
      setSnackbar({ open: true, message: 'Clarification note is required', severity: 'error' });
      return;
    }
    try {
      setActionLoading(true);
      await kycAPI.reject(selectedKYC.id, {
        verification_status: 'rejected',
        rejection_reason: `[CLARIFICATION NEEDED] ${clarificationNote}`,
        verification_notes: verificationNotes || null,
      });
      setSnackbar({ open: true, message: 'Clarification requested — farmer notified to resubmit', severity: 'info' });
      setClarifyDialogOpen(false);
      setClarificationNote('');
      setSelectedKYC(null);
      setKycDetails(null);
      await loadData(true);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to request clarification', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // ── Stats ──
  const kpiStats = useMemo(() => {
    if (!stats) return { total: 0, pending: 0, underReview: 0, approved: 0, rejected: 0 };
    return {
      total: stats.total || 0,
      pending: stats.pending || 0,
      underReview: stats.under_review || 0,
      approved: stats.approved || 0,
      rejected: stats.rejected || 0,
    };
  }, [stats]);

  // ── Filter ──
  const filteredSubmissions = useMemo(() => {
    return submissions.filter(s => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const match = (s.farmer_name || '').toLowerCase().includes(q)
          || (s.document_number_masked || '').toLowerCase().includes(q)
          || (s.document_type || '').toLowerCase().includes(q)
          || String(s.id).includes(q)
          || String(s.farmer_id).includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [submissions, searchTerm]);

  // Risk flags for selected KYC
  const riskFlags = useMemo(() => {
    if (!selectedKYC) return [];
    return deriveRiskFlags(selectedKYC, submissions);
  }, [selectedKYC, submissions]);

  // ── Document preview URLs ──
  const previewDocs = useMemo(() => {
    const detail = kycDetails || selectedKYC;
    if (!detail) return [];
    const docs = [];
    if (detail.document_front_url) docs.push({ label: 'Front', url: detail.document_front_url });
    if (detail.document_back_url)  docs.push({ label: 'Back',  url: detail.document_back_url });
    if (detail.selfie_url)         docs.push({ label: 'Selfie', url: detail.selfie_url });
    return docs;
  }, [kycDetails, selectedKYC]);

  // The active doc to resolve details from (details API enriches the data)
  const activeKYC = kycDetails || selectedKYC;

  // ── Loading ──
  if (loading && submissions.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading KYC data…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ 1. KPI PIPELINE STRIP ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-3">
        <KPICard icon={<VerifiedUser />} label="Total Submitted" value={kpiStats.total} color="#2563eb"
          onClick={() => setStatusFilter('all_pending')} active={statusFilter === 'all_pending'} />
        <KPICard icon={<AccessTime />} label="Pending" value={kpiStats.pending} color="#d97706"
          onClick={() => setStatusFilter('pending')} active={statusFilter === 'pending'} />
        <KPICard icon={<HourglassBottom />} label="Under Review" value={kpiStats.underReview} color="#7c3aed"
          onClick={() => setStatusFilter('under_review')} active={statusFilter === 'under_review'} />
        <KPICard icon={<CheckCircle />} label="Approved" value={kpiStats.approved} color="#16a34a"
          onClick={() => setStatusFilter('approved')} active={statusFilter === 'approved'} />
        <KPICard icon={<Cancel />} label="Rejected" value={kpiStats.rejected} color="#dc2626"
          onClick={() => setStatusFilter('rejected')} active={statusFilter === 'rejected'} />
      </Box>

      {/* ══════════ 2. RISK FLAGS ══════════ */}
      {selectedKYC && riskFlags.length > 0 && (
        <Card sx={{ borderRadius: 2, mb: 2, border: '2px solid #fca5a5', bgcolor: '#fef2f2' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box className="flex items-center gap-2">
              <Flag sx={{ fontSize: 16, color: '#dc2626' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Risk Flags Detected
              </Typography>
              <Box className="flex items-center gap-1.5 ml-2">
                {riskFlags.map(flagKey => {
                  const flag = RISK_FLAGS.find(f => f.key === flagKey);
                  if (!flag) return null;
                  return (
                    <Chip key={flag.key} label={flag.label} size="small" icon={flag.icon}
                      sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: flag.bg, color: flag.color, '& .MuiChip-icon': { color: flag.color } }} />
                  );
                })}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ══════════ FILTER BAR ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box className="flex flex-wrap items-center gap-2">
            <TextField
              size="small" placeholder="Search farmer, doc number, ID…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            />
            <Button size="small" variant={showFilters ? 'contained' : 'outlined'}
              onClick={() => setShowFilters(!showFilters)} startIcon={<FilterList />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Filters {showFilters ? <ExpandLess sx={{ ml: 0.5 }} /> : <ExpandMore sx={{ ml: 0.5 }} />}
            </Button>

            <Box className="flex-1" />
            <Tooltip title="Refresh"><IconButton size="small" onClick={() => loadData(true)} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
            <Chip label={`${filteredSubmissions.length} of ${submissions.length}`} size="small" sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Box>

          <Collapse in={showFilters}>
            <Box className="flex flex-wrap items-center gap-2 mt-2 pt-2" sx={{ borderTop: '1px solid #e2e8f0' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all_pending">All Pending</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              {(statusFilter !== 'all_pending' || searchTerm) && (
                <Button size="small" onClick={() => { setStatusFilter('all_pending'); setSearchTerm(''); }}
                  sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600 }}>Clear All</Button>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ══════════ 3. THREE-COLUMN LAYOUT ══════════ */}
      <Box className="flex gap-2.5" sx={{ height: 'calc(100vh - 330px)', minHeight: 480 }}>

        {/* ── LEFT: KYC Queue ── */}
        <Card sx={{ flex: '0 0 280px', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              KYC Queue ({filteredSubmissions.length})
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredSubmissions.length === 0 ? (
              <Box className="flex flex-col items-center justify-center h-full" sx={{ px: 2 }}>
                <VerifiedUser sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>
                  {statusFilter === 'all_pending' || statusFilter === 'pending'
                    ? 'Verification queue is clear!'
                    : 'No submissions match this filter'}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1', textAlign: 'center', mt: 0.5 }}>
                  {statusFilter === 'all_pending' || statusFilter === 'pending'
                    ? 'All KYC submissions have been processed.'
                    : 'Try adjusting the filters above.'}
                </Typography>
              </Box>
            ) : (
              filteredSubmissions.map((kyc) => {
                const st = STATUS_CONF[kyc.verification_status] || STATUS_CONF.pending;
                const dt = DOC_TYPE_CONF[kyc.document_type] || { label: kyc.document_type, icon: <Description sx={{ fontSize: 14 }} />, color: '#64748b' };
                const isActive = selectedKYC?.id === kyc.id;
                const flags = deriveRiskFlags(kyc, submissions);

                return (
                  <Box
                    key={kyc.id}
                    onClick={() => handleSelectKYC(kyc)}
                    sx={{
                      px: 1.5, py: 1.2, cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: `3px solid ${isActive ? '#7c3aed' : st.border}`,
                      bgcolor: isActive ? '#f5f3ff' : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: isActive ? '#f5f3ff' : '#f8fafc' },
                    }}
                  >
                    <Box className="flex items-start gap-1.5">
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <Person sx={{ fontSize: 16, color: '#64748b' }} />
                      </Box>
                      <Box className="min-w-0 flex-1">
                        <Box className="flex items-center gap-1">
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {kyc.farmer_name || `Farmer #${kyc.farmer_id}`}
                          </Typography>
                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', flexShrink: 0 }}>
                            {kycAge(kyc.submitted_at)}
                          </Typography>
                        </Box>
                        <Box className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <Chip label={dt.label} size="small" icon={dt.icon}
                            sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: `${dt.color}10`, color: dt.color, '& .MuiChip-icon': { color: dt.color, fontSize: 12 } }} />
                          <Chip label={st.label} size="small" icon={st.icon}
                            sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: st.bg, color: st.color, '& .MuiChip-icon': { color: st.color, fontSize: 12 } }} />
                          {flags.length > 0 && (
                            <Tooltip title={`${flags.length} risk flag(s)`}>
                              <Flag sx={{ fontSize: 12, color: '#dc2626' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontFamily: 'monospace', mt: 0.3 }}>
                          {kyc.document_number_masked} • #{kyc.id}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Card>

        {/* ── CENTER: Document Preview ── */}
        <Card sx={{ flex: 1, borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selectedKYC ? (
            <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1' }}>
              <Fingerprint sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#94a3b8' }}>Select a KYC submission to preview</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', mt: 0.5 }}>Click on any item in the queue</Typography>
            </Box>
          ) : (
            <>
              {/* Preview Header */}
              <Box sx={{ px: 2, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
                <Box className="flex items-center justify-between">
                  <Box className="flex items-center gap-2 min-w-0">
                    <Person sx={{ fontSize: 16, color: '#94a3b8' }} />
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}>
                      {selectedKYC.farmer_name || `Farmer #${selectedKYC.farmer_id}`}
                    </Typography>
                    {(() => {
                      const dt = DOC_TYPE_CONF[selectedKYC.document_type] || { label: selectedKYC.document_type, color: '#64748b' };
                      return <Chip label={dt.label} size="small" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${dt.color}20`, color: dt.color }} />;
                    })()}
                  </Box>
                  <IconButton size="small" onClick={() => { setSelectedKYC(null); setKycDetails(null); }} sx={{ color: '#94a3b8' }}>
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Document Tabs */}
              {previewDocs.length > 0 && (
                <Box sx={{ bgcolor: '#1e293b', flexShrink: 0 }}>
                  <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)}
                    sx={{
                      minHeight: 32,
                      '& .MuiTab-root': { color: '#64748b', minHeight: 32, fontSize: '0.7rem', textTransform: 'none', fontWeight: 600, py: 0.5 },
                      '& .Mui-selected': { color: '#fff' },
                      '& .MuiTabs-indicator': { bgcolor: '#7c3aed' },
                    }}>
                    {previewDocs.map((doc, i) => (
                      <Tab key={i} label={doc.label} />
                    ))}
                  </Tabs>
                </Box>
              )}

              {/* Preview Area */}
              <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {detailsLoading ? (
                  <Box className="flex flex-col items-center">
                    <Box sx={{ width: 32, height: 32, border: '3px solid #475569', borderTop: '3px solid #7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                    <Typography sx={{ color: '#64748b', mt: 1, fontSize: '0.72rem' }}>Loading details…</Typography>
                  </Box>
                ) : previewDocs.length > 0 && previewDocs[previewTab] ? (
                  <Box
                    component="img"
                    src={previewDocs[previewTab].url}
                    alt={previewDocs[previewTab].label}
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', p: 2 }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div style="text-align:center;color:#94a3b8;font-size:0.8rem;">Image could not be loaded.<br/>The URL may have expired or be inaccessible.</div>';
                    }}
                  />
                ) : (
                  <Box className="text-center">
                    <Visibility sx={{ fontSize: 48, color: '#475569', mb: 1 }} />
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>No document images available</Typography>
                  </Box>
                )}
              </Box>

              {/* KYC Timeline + Audit Log below preview */}
              <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexShrink: 0 }}>
                <Box className="flex gap-3">
                  <Box sx={{ flex: 1 }}>
                    <KYCTimeline kyc={activeKYC} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <AuditLog kyc={activeKYC} />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Card>

        {/* ── RIGHT: Decision Panel ── */}
        <Card sx={{ flex: '0 0 290px', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Decision Panel
            </Typography>
          </Box>

          {!selectedKYC ? (
            <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1', px: 2 }}>
              <Shield sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>
                Select a KYC to review
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column' }}>

              {/* Farmer Info */}
              <Section icon={<Person />} title="Farmer Details" color="#2563eb">
                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0', mb: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <InfoRow label="Name" value={activeKYC?.farmer?.name || selectedKYC.farmer_name || '—'} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="ID" value={`#${selectedKYC.farmer_id}`} />
                    {activeKYC?.farmer?.phone && (<><Divider sx={{ my: 0.5 }} /><InfoRow label="Phone" value={activeKYC.farmer.phone} /></>)}
                    {activeKYC?.farmer?.email && (<><Divider sx={{ my: 0.5 }} /><InfoRow label="Email" value={activeKYC.farmer.email} /></>)}
                    {activeKYC?.farmer?.address && (<><Divider sx={{ my: 0.5 }} /><InfoRow label="Address" value={activeKYC.farmer.address} /></>)}
                  </CardContent>
                </Card>
              </Section>

              {/* Document Info */}
              <Section icon={<Badge />} title="Document" color="#7c3aed">
                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0', mb: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <InfoRow label="Type" value={(DOC_TYPE_CONF[selectedKYC.document_type] || {}).label || selectedKYC.document_type} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Number" value={
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>
                        {activeKYC?.document_number || selectedKYC.document_number_masked}
                      </Typography>
                    } />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="KYC Type" value={selectedKYC.kyc_type} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Status" value={
                      (() => {
                        const st = STATUS_CONF[selectedKYC.verification_status] || STATUS_CONF.pending;
                        return <Chip label={st.label} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: st.bg, color: st.color }} />;
                      })()
                    } />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Submitted" value={fmtDate(selectedKYC.submitted_at)} />
                    {activeKYC?.document_expiry_date && (
                      <><Divider sx={{ my: 0.5 }} /><InfoRow label="Expiry" value={fmtDate(activeKYC.document_expiry_date)} /></>
                    )}
                  </CardContent>
                </Card>
              </Section>

              {/* Risk Flags in panel */}
              {riskFlags.length > 0 && (
                <Section icon={<Flag />} title="Risk Flags" color="#dc2626">
                  <Box className="flex flex-wrap gap-1 mb-2">
                    {riskFlags.map(flagKey => {
                      const flag = RISK_FLAGS.find(f => f.key === flagKey);
                      if (!flag) return null;
                      return (
                        <Chip key={flag.key} label={flag.label} size="small" icon={flag.icon}
                          sx={{ height: 22, fontSize: '0.6rem', fontWeight: 700, bgcolor: flag.bg, color: flag.color, '& .MuiChip-icon': { color: flag.color } }} />
                      );
                    })}
                  </Box>
                </Section>
              )}

              {/* Internal Note */}
              {(selectedKYC.verification_status === 'pending' || selectedKYC.verification_status === 'under_review') && (
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center gap-1 mb-1">
                    <StickyNote2 sx={{ fontSize: 14, color: '#64748b' }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Internal Note
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth size="small" multiline rows={2}
                    placeholder="Add internal verification notes…"
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                  />
                </Box>
              )}

              {/* Existing rejection reason */}
              {selectedKYC.rejection_reason && (
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center gap-1 mb-1">
                    <Cancel sx={{ fontSize: 14, color: '#dc2626' }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase' }}>
                      Rejection Reason
                    </Typography>
                  </Box>
                  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#fca5a5' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#dc2626', fontStyle: 'italic' }}>
                        "{selectedKYC.rejection_reason}"
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Action Buttons */}
              {(selectedKYC.verification_status === 'pending' || selectedKYC.verification_status === 'under_review') ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                  {selectedKYC.verification_status === 'pending' && (
                    <Button fullWidth variant="outlined" size="small" startIcon={<HourglassBottom />}
                      onClick={handleStartReview} disabled={actionLoading}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { bgcolor: '#f5f3ff', borderColor: '#6d28d9' } }}>
                      Start Review
                    </Button>
                  )}
                  <Button fullWidth variant="contained" size="small" startIcon={<CheckCircle />}
                    onClick={handleApprove} disabled={actionLoading}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
                    Approve
                  </Button>

                  {/* Rejection with inline reason */}
                  <TextField
                    fullWidth size="small" multiline rows={2}
                    placeholder="Rejection reason (required to reject)…"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fef2f2', fontSize: '0.72rem' } }}
                  />
                  <Button fullWidth variant="contained" size="small" startIcon={<Cancel />}
                    onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                    Reject
                  </Button>
                  <Button fullWidth variant="outlined" size="small" startIcon={<QuestionAnswer />}
                    onClick={() => setClarifyDialogOpen(true)} disabled={actionLoading}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, borderColor: '#d97706', color: '#d97706', '&:hover': { bgcolor: '#fef3c7', borderColor: '#b45309' } }}>
                    Request Clarification
                  </Button>
                </Box>
              ) : (
                <Box sx={{ pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                  <Alert severity={selectedKYC.verification_status === 'approved' ? 'success' : 'error'}
                    sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    This KYC has been {selectedKYC.verification_status === 'approved' ? 'approved' : 'rejected'}.
                    {selectedKYC.verifier_name && ` by ${selectedKYC.verifier_name}`}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </Box>

      {/* ══════════ CLARIFICATION DIALOG ══════════ */}
      <Dialog open={clarifyDialogOpen} onClose={() => setClarifyDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#fef3c7', fontWeight: 700, fontSize: '1rem', color: '#92400e' }}>
          Request Clarification
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mb: 2 }}>
            The farmer will be notified to resubmit their KYC documents with the requested clarification.
          </Typography>
          <TextField
            autoFocus fullWidth multiline rows={3} size="small"
            label="What needs to be clarified? *"
            value={clarificationNote}
            onChange={(e) => setClarificationNote(e.target.value)}
            placeholder="e.g., Aadhaar photo is blurry, please upload a clearer copy…"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClarifyDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleRequestClarification} disabled={actionLoading || !clarificationNote.trim()}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
            Send Clarification Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KYCReview;
